import { useEffect, useState, useSyncExternalStore } from 'react';
import { QM_MIC_METER as M } from './constants';

export interface MicLevel {
  /** Lit meter segments, 0..QM_MIC_METER.SEGMENTS. */
  segments: number;
  /** Nothing voice-like for QM_MIC_METER.QUIET_MS: noise or silence only. */
  quiet: boolean;
}

export interface MicLevelStore {
  subscribe: (onChange: () => void) => () => void;
  get: () => MicLevel;
}

const SILENT: MicLevel = { segments: 0, quiet: false };

function createStore() {
  let value = SILENT;
  const listeners = new Set<() => void>();
  return {
    subscribe(onChange: () => void) {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    get: () => value,
    set(next: MicLevel) {
      if (next.segments === value.segments && next.quiet === value.quiet) return;
      value = next;
      listeners.forEach((l) => l());
    },
  };
}

function rmsDb(analyser: AnalyserNode, buf: Float32Array<ArrayBuffer>): number {
  analyser.getFloatTimeDomainData(buf);
  let sum = 0;
  for (const v of buf) sum += v * v;
  return 20 * Math.log10(Math.max(Math.sqrt(sum / buf.length), 1e-8));
}

function percentile(sorted: readonly number[], p: number): number {
  return sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]!;
}

function segmentsFor(db: number): number {
  const norm = (db - M.METER_MIN_DB) / (M.METER_MAX_DB - M.METER_MIN_DB);
  return Math.max(0, Math.min(M.SEGMENTS, Math.ceil(norm * M.SEGMENTS)));
}

/**
 * Ticks on the engine's own analyser, read afresh each tick: it is the recording's input, so the
 * meter always shows the mic being recorded and never opens a capture of its own.
 */
function startMeter(getAnalyser: () => AnalyserNode | null, report: (level: MicLevel) => void): () => void {
  let buf: Float32Array<ArrayBuffer> | null = null;
  const recent: number[] = [];
  let shown = -Infinity;
  let loudTicks = 0;
  let heardAt = Date.now();
  let lastTick = Date.now();
  let lastReport = 0;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  const id = window.setInterval(() => {
    const now = Date.now();
    if (now - lastTick > M.TICK_GAP_MS) heardAt = now;
    lastTick = now;
    const analyser = getAnalyser();
    // No input yet, or a graph that isn't running, reads silence; that is not the room going quiet.
    if (!analyser || analyser.context.state !== 'running') {
      heardAt = now;
      report(SILENT);
      return;
    }
    if (buf?.length !== analyser.fftSize) buf = new Float32Array(analyser.fftSize);
    const db = rmsDb(analyser, buf);
    recent.push(db);
    if (recent.length > M.FLOOR_WINDOW_TICKS) recent.shift();
    if (recent.length < M.WARMUP_TICKS) {
      heardAt = now;
    } else {
      const sorted = [...recent].sort((a, b) => a - b);
      const floor = percentile(sorted, M.FLOOR_PERCENTILE);
      const spread = percentile(sorted, M.SPREAD_HIGH_PERCENTILE) - floor;
      // Level alone is not voice: a hum can sit far above any threshold. Speech rises and falls,
      // so a loud reading counts only while the window keeps moving.
      const loud =
        (db >= M.SPEECH_LOUD_DB && spread >= M.LOUD_MIN_SPREAD_DB) ||
        (db > M.SPEECH_MIN_DB && db - floor >= M.SPEECH_OVER_FLOOR_DB);
      loudTicks = loud ? loudTicks + 1 : 0;
      if (loudTicks >= M.SPEECH_MIN_TICKS) heardAt = now;
    }
    shown = Math.max(db, shown - M.DECAY_DB_PER_TICK);
    if (reducedMotion?.matches && now - lastReport < M.REDUCED_MOTION_TICK_MS) return;
    lastReport = now;
    report({ segments: segmentsFor(shown), quiet: now - heardAt >= M.QUIET_MS });
  }, M.TICK_MS);

  return () => window.clearInterval(id);
}

/** Live mic level and the no-speech check, only while `active` (recording, not paused). */
export function useMicLevel(active: boolean, getAnalyser: () => AnalyserNode | null): MicLevelStore {
  const [store] = useState(createStore);
  useEffect(() => {
    if (!active) return;
    const stop = startMeter(getAnalyser, store.set);
    return () => {
      stop();
      store.set(SILENT);
    };
  }, [active, getAnalyser, store]);
  return store;
}

export const useMicSegments = (store: MicLevelStore) =>
  useSyncExternalStore(store.subscribe, () => store.get().segments);

export const useMicQuiet = (store: MicLevelStore) => useSyncExternalStore(store.subscribe, () => store.get().quiet);
